import { useState, useEffect, useCallback } from "react";
import {
  workPerformanceApi,
  loadStepOptions, loadGradeOrder,
  stepOptionApi,
} from "./workPerformanceApi";

import StepConfigPanel from "../../components/work_performance/StepConfigPanel";

import useIsMobile      from "./components/useIsMobile";
import QuickFormHeader  from "./components/QuickFormHeader";
import StepFolderGrid   from "./components/StepFolderGrid";

const toKST = (d) => new Date(d.getTime() + 9 * 60 * 60 * 1000);
const todayStr = () => toKST(new Date()).toISOString().slice(0, 10);

const STAFF_STEP_KEYS = ["deputy_report", "team_report", "colleague"];

export default function WorkPerformanceQuickPage() {
  const isMobile = useIsMobile();

  const { currentUser, currentGrade, currentUserId } = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("user"));
      if (!u) return { currentUser: null, currentGrade: null, currentUserId: null };
      return {
        currentUser:  u.name || u.username || null,
        currentGrade: u.position || u.grade || null,
        currentUserId: u.id ?? null,
      };
    } catch { return { currentUser: null, currentGrade: null, currentUserId: null }; }
  })();

  const [date,      setDate]      = useState(todayStr());
  const [saving,    setSaving]    = useState(false);

  const [title,      setTitle]      = useState("");
  const [difficulty, setDifficulty] = useState(5);   // 1~10
  const [selSteps,   setSelSteps]   = useState([]);

  const [stepOptions, setStepOptions] = useState([]);

  // 대리 이상 여부 (position_rank <= 3)
  const canEditStepOptions = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("user"));
      return u && (u.position_rank ?? 99) <= 3;
    } catch { return false; }
  })();

  const [showStepConfig, setShowStepConfig] = useState(false);

  // 📂 폴더 열림/닫힘 상태 (모바일은 기본 접힘 → 탐색 우선)
  const [openFolders, setOpenFolders] = useState(
    isMobile ? {} : { "기본 폴더": true, "보고": true }
  );

  const stepMap = Object.fromEntries(stepOptions.map(s => [s.key, s]));

  // 폴더별 그룹화
  const groupedFolderOptions = stepOptions.reduce((acc, opt) => {
    const fName = opt.folder || "기본 폴더";
    if (!acc[fName]) acc[fName] = [];
    acc[fName].push(opt);
    return acc;
  }, {});

  // 서버에서 step options + grade order 로드
  useEffect(() => {
    loadStepOptions().then(opts => setStepOptions(opts));
    loadGradeOrder();
  }, []);

  const moveDate = (delta) => {
    const d = new Date(date);
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().slice(0, 10));
  };

  // 항목 선택/해제
  const toggleStep = (key) => {
    setSelSteps(prev => {
      const exists = prev.find(s => s.key === key);
      if (exists) return prev.filter(s => s.key !== key);

      const config = stepMap[key];
      return [...prev, {
        key,
        targets: [config?.defaultTarget || ""],
        note: config?.defaultNote || "",
        minutes: config?.defaultMin ?? 5,
        count: 1,            // 기본 건수 1
        staffId: null,
        staffName: "",
      }];
    });
  };

  const addTargetInput = (key) => {
    setSelSteps(prev => prev.map(s =>
      s.key === key ? { ...s, targets: [...s.targets, ""] } : s
    ));
  };

  const removeTargetInput = (key, index) => {
    setSelSteps(prev => prev.map(s => {
      if (s.key !== key) return s;
      if (s.targets.length <= 1) return { ...s, targets: [""] };
      return { ...s, targets: s.targets.filter((_, i) => i !== index) };
    }));
  };

  const updateTargetValue = (key, index, value) => {
    setSelSteps(prev => prev.map(s => {
      if (s.key !== key) return s;
      const newTargets = [...s.targets];
      newTargets[index] = value;
      return { ...s, targets: newTargets };
    }));
  };

  const toggleFolder = (folderName) => {
    setOpenFolders(prev => ({ ...prev, [folderName]: !prev[folderName] }));
  };

  const updateStep = (key, field, value) =>
    setSelSteps(prev => prev.map(s => s.key === key ? { ...s, [field]: value } : s));

  const resetForm = () => { setTitle(""); setDifficulty(5); setSelSteps([]); };

  const handleAddStep = async (newStepData) => {
    const key = "custom_" + Date.now();
    const sameFolder = stepOptions.filter(o => (o.folder || "기본 폴더") === (newStepData.folder || "기본 폴더"));
    const order = sameFolder.length > 0 ? Math.max(...sameFolder.map(o => o.order ?? 0)) + 1 : stepOptions.length;
    try {
      await stepOptionApi.create({
        key,
        label:       newStepData.label,
        icon:        newStepData.icon,
        color:       newStepData.color,
        default_min: newStepData.defaultMin,
        folder:      newStepData.folder || "기본 폴더",
        order,
      });
      const opts = await loadStepOptions();
      setStepOptions(opts);
    } catch (e) { alert("항목 추가 실패: " + e.message); }
  };

  const removeStep = async (key) => {
    const target = stepOptions.find(o => o.key === key);
    if (!target) return;
    try {
      await stepOptionApi.remove(target.id);
      const opts = await loadStepOptions();
      setStepOptions(opts);
      setSelSteps(prev => prev.filter(s => s.key !== key));
    } catch (e) { alert("항목 삭제 실패: " + e.message); }
  };

  // 항목 수정(라벨/폴더/아이콘/색상/소요시간) → 서버 PATCH 후 재로드
  // 주의: patch는 snake_case (default_min, folder, label, icon, color)
  const handleUpdateStep = async (id, patch) => {
    try {
      await stepOptionApi.update(id, patch);
      const opts = await loadStepOptions();
      setStepOptions(opts);
    } catch (e) { alert("항목 수정 실패: " + e.message); }
  };



  const resetSteps = async () => {
    try {
      await stepOptionApi.removeAll();
      setStepOptions([]);
      setSelSteps([]);
      setOpenFolders({ "기본 폴더": true });
    } catch (e) { alert("전체 삭제 실패: " + e.message); }
  };

  const handleReorderSteps = async (newSteps) => {
    setStepOptions(newSteps);
    try {
      await stepOptionApi.reorder(newSteps);
    } catch (e) { console.error("순서 저장 실패:", e); }
  };

  const handleSubmit = async () => {
    if (!title.trim()) { alert("업무 제목을 입력해주세요."); return; }
    if (selSteps.length === 0) { alert("처리 단계를 하나 이상 선택해주세요."); return; }

    for (const s of selSteps) {
      const stepConfig = stepMap[s.key];
      if (STAFF_STEP_KEYS.includes(s.key) && !s.staffId) {
        alert(`"${stepMap[s.key]?.label}" 단계의 대상자를 입력해주세요.`);
        return;
      }
      if (stepConfig?.inputType === "staff" && !String(s.note || "").trim()) {
        alert(`"${stepConfig.label}" 항목의 담당자를 입력해주세요.`);
        return;
      }
    }

    setSaving(true);
    try {
      const ordered = stepOptions.filter(o => selSteps.find(s => s.key === o.key));
      const payload = {
        title, content: "", work_date: date, difficulty: Number(difficulty) || 5,
        steps: ordered.map((o, i) => {
          const s = selSteps.find(s => s.key === o.key);
          const mergedTarget = s.targets ? s.targets.filter(t => t.trim() !== "").join(", ") : "";
          return {
            step_type: o.key,
            order: i,
            note: JSON.stringify({ target: mergedTarget, content: s.note || "" }),
            staff_id: s.staffId ?? null,
            minutes: s.minutes ?? o.defaultMin,
            count: s.count ?? 1,
          };
        }),
      };
      await workPerformanceApi.create(payload);
      resetForm();
      alert("등록되었습니다.");
    } catch (e) { alert("저장 실패: " + e.message); }
    finally { setSaving(false); }
  };

  const isToday = date === todayStr();

  const dateLabel = (() => {
    const d = new Date(date + "T00:00:00");
    const days = ["일", "월", "화", "수", "목", "금", "토"];
    return `${date} (${days[d.getDay()]})`;
  })();

  const navBtnStyle = {
    padding: isMobile ? "6px 12px" : "4px 10px", borderRadius: 6,
    border: "0.5px solid #e2e8f0", background: "#fff",
    color: "#0f172a", fontSize: 15, cursor: "pointer",
    whiteSpace: "nowrap", flexShrink: 0, flexGrow: 0,
    lineHeight: "normal", display: "inline-block",
    width: "fit-content", minWidth: 0,
  };
  const btnPrimary = {
    padding: isMobile ? "12px 24px" : "6px 16px", borderRadius: 6, border: "none",
    background: "#1e293b", color: "#fff",
    fontSize: isMobile ? 15 : 13, fontWeight: 500, cursor: "pointer",
    whiteSpace: "nowrap", flexShrink: 0, flexGrow: isMobile ? 1 : 0,
    width: isMobile ? "auto" : "fit-content", display: "inline-block",
  };
  const btnAction = {
    padding: isMobile ? "12px 18px" : "6px 14px", borderRadius: 6,
    border: "0.5px solid #e2e8f0", background: "#ffffff2f",
    color: "#0f172a", fontSize: isMobile ? 15 : 13, cursor: "pointer",
    whiteSpace: "nowrap", flexShrink: 0, flexGrow: 0,
    width: "fit-content", display: "inline-block",
  };

  return (
    <div style={{
      padding: isMobile ? "12px 14px 88px" : "16px 20px", // 모바일은 하단 sticky 바 높이만큼 여백
      fontFamily: "'Noto Sans KR', sans-serif",
      overflowX: "hidden", color: "#0f172a",
    }}>

      {/* 헤더 (날짜 네비) */}
      <div style={{
        display: "flex", alignItems: "center", gap: 6, marginBottom: 14,
        flexWrap: isMobile ? "wrap" : "nowrap", width: isMobile ? "100%" : "fit-content",
      }}>
        <span style={{
          fontSize: isMobile ? 22 : 28, fontWeight: 600, color: "#0f172a",
          whiteSpace: "nowrap", flexShrink: 0,
          width: isMobile ? "100%" : "auto", marginBottom: isMobile ? 4 : 0,
        }}>
          업무 빠른 입력
        </span>
        <span style={{
          fontSize: 15, color: "#0f172a", fontWeight: 500, whiteSpace: "nowrap", flexShrink: 0,
          background: "#f1f5f9", border: "0.5px solid #e2e8f0", borderRadius: 6, padding: "4px 10px",
        }}>{dateLabel}</span>
        <button style={navBtnStyle} onClick={() => moveDate(-1)}>← 전날</button>
        {!isToday && <button style={navBtnStyle} onClick={() => setDate(todayStr())}>오늘</button>}
        <button style={navBtnStyle} onClick={() => moveDate(1)}>다음날 →</button>

        {canEditStepOptions && (
          <button
            style={{
              ...navBtnStyle,
              color: showStepConfig ? "#2563eb" : "#0f172a",
              fontWeight: showStepConfig ? "600" : "normal",
            }}
            onClick={() => setShowStepConfig(v => !v)}
          >
            ➕ 항목 추가
          </button>
        )}
      </div>
      
      {showStepConfig && (
        <StepConfigPanel
          stepOptions={stepOptions}
          onAddStep={handleAddStep}
          onRemoveStep={removeStep}
          onUpdateStep={handleUpdateStep}
          onResetSteps={resetSteps}
          onReorderSteps={handleReorderSteps}
          onClose={() => setShowStepConfig(false)}
        />
      )}


      <div>
        <div style={{
          background: "#fff", border: "0.5px solid #e2e8f0",
          borderRadius: 12, padding: isMobile ? "12px 14px" : "14px 16px",
          width: "100%", boxSizing: "border-box",
        }}>

          <QuickFormHeader
            title={title}
            difficulty={difficulty}
            onChangeTitle={setTitle}
            onChangeDifficulty={setDifficulty}
            isMobile={isMobile}
          />

          <StepFolderGrid
            groupedFolderOptions={groupedFolderOptions}
            selSteps={selSteps}
            openFolders={openFolders}
            isMobile={isMobile}
            onToggleFolder={toggleFolder}
            onToggleStep={toggleStep}
            onAddTarget={addTargetInput}
            onRemoveTarget={removeTargetInput}
            onUpdateTarget={updateTargetValue}
            onUpdateStep={updateStep}
          />

          {/* PC 전용: 하단 인라인 액션 바 */}
          {!isMobile && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 10 }}>
              <span style={{ fontSize: 12, color: "#0f172a", fontWeight: 600, whiteSpace: "nowrap" }}>
                👤 {currentUser || "—"}{currentGrade ? ` · ${currentGrade}` : ""}
              </span>
              <button style={btnAction} onClick={resetForm}>초기화</button>
              <button style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={handleSubmit} disabled={saving}>
                {saving ? "저장 중…" : "등록"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 모바일 전용: 하단 고정(sticky) 액션 바 — 한 손 입력/빠른 등록 우선 */}
      {isMobile && (
        <div style={{
          position: "fixed", left: 0, right: 0, bottom: 0,
          background: "#fff", borderTop: "0.5px solid #e2e8f0",
          padding: "10px 14px", display: "flex", alignItems: "center", gap: 8,
          boxShadow: "0 -2px 8px rgba(0,0,0,0.04)", zIndex: 50,
          boxSizing: "border-box",
        }}>
          <span style={{ fontSize: 12, color: "#0f172a", fontWeight: 600, whiteSpace: "nowrap" }}>
            👤 {currentUser || "—"}
          </span>
          <button style={btnAction} onClick={resetForm}>초기화</button>
          <button
            style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? "저장 중…" : "등록"}
          </button>
        </div>
      )}
    </div>
  );
}